import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { EnvelopeSimple, Flame, Warning, Trophy, ArrowLeft, ArrowRight, TrendDown, TrendUp, Barbell, Heartbeat } from "@phosphor-icons/react";

function getWeekStart(d = new Date()) {
  const day = d.getDay();
  const diff = (day + 6) % 7;
  const monday = new Date(d);
  monday.setDate(d.getDate() - diff);
  return monday.toISOString().slice(0, 10);
}

function shiftWeek(iso, delta) {
  const d = new Date(iso + "T00:00:00Z");
  d.setDate(d.getDate() + delta * 7);
  return d.toISOString().slice(0, 10);
}

export default function CoachDigest() {
  const [weekStart, setWeekStart] = useState(getWeekStart());
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/coach/digest?week_start=${weekStart}`);
      setDigest(data);
    } catch (e) {
      toast.error("Could not load digest");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const emailSend = () => {
    toast.info("Sunday auto-email — added to waitlist. Coming with Resend integration.");
  };

  return (
    <Layout>
      <div className="p-8 md:p-12">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">/ Weekly Digest</div>
            <h1 className="font-heading font-black uppercase text-5xl mt-2 flex items-center gap-4">
              <EnvelopeSimple size={48} weight="duotone" className="text-[#FF3B30]" /> Sunday Recap
            </h1>
            <p className="text-[#A3A3A3] mt-2">Every player. Sessions, readiness dips, missed workouts, and match ratings.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setWeekStart(shiftWeek(weekStart, -1))} data-testid="prev-week"
              className="border border-[#262626] p-2 hover:border-[#FF3B30] text-[#A3A3A3] hover:text-white">
              <ArrowLeft size={18} />
            </button>
            <div className="border border-[#FF3B30] bg-[#FF3B30]/10 px-4 py-2 text-sm font-heading uppercase tracking-widest" data-testid="digest-week">
              Week of {weekStart}
            </div>
            <button onClick={() => setWeekStart(shiftWeek(weekStart, 1))}
              disabled={weekStart >= getWeekStart()}
              data-testid="next-week"
              className="border border-[#262626] p-2 hover:border-[#FF3B30] text-[#A3A3A3] hover:text-white disabled:opacity-30">
              <ArrowRight size={18} />
            </button>
            <Button onClick={emailSend} data-testid="email-send"
              className="rounded-none bg-[#121212] border border-[#262626] hover:border-[#FF3B30] text-[#A3A3A3] hover:text-white font-heading uppercase tracking-widest font-bold h-10 px-4 ml-2">
              <EnvelopeSimple size={16} className="mr-2" /> Email Me
              <span className="ml-2 text-[10px] border border-[#FF9F0A] text-[#FF9F0A] px-1">Waitlist</span>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-[#A3A3A3]" data-testid="digest-loading">Loading digest…</div>
        ) : !digest || digest.players.length === 0 ? (
          <div className="border-2 border-dashed border-[#262626] p-16 text-center" data-testid="digest-empty">
            <EnvelopeSimple size={48} className="text-[#A3A3A3] mx-auto mb-4" />
            <div className="font-heading uppercase tracking-widest text-[#A3A3A3]">No players linked</div>
            <div className="text-sm text-[#A3A3A3] mt-2">
              Invite players to see their weekly stats here.
              <br />
              <Link to="/coach" className="text-[#FF3B30] underline">Get an invite code →</Link>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatBox label="Players" value={digest.totals.player_count} />
              <StatBox label="Total Sessions" value={digest.totals.total_sessions} icon={Barbell} />
              <StatBox label="Under-Recovered" value={digest.totals.players_with_dip} icon={Heartbeat} color={digest.totals.players_with_dip > 0 ? "#FF3B30" : null} />
              <StatBox label="Missing Workouts" value={digest.totals.players_missing_workouts} icon={Warning} color={digest.totals.players_missing_workouts > 0 ? "#FF9F0A" : null} />
            </div>

            {/* Highlights */}
            <div className="grid md:grid-cols-2 gap-4">
              {digest.top_performer && (
                <div className="border border-[#34C759] bg-[#34C759]/5 p-6" data-testid="top-performer">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendUp size={20} weight="fill" className="text-[#34C759]" />
                    <div className="text-xs font-heading uppercase tracking-widest text-[#34C759]">Top Performer</div>
                  </div>
                  <div className="font-heading font-black text-3xl">{digest.top_performer.name}</div>
                  <div className="text-sm text-[#A3A3A3] mt-1">
                    {digest.top_performer.sessions} sessions · <Flame size={14} weight="fill" className="inline text-[#FF3B30]" /> {digest.top_performer.streak} streak
                  </div>
                </div>
              )}
              {digest.biggest_dip && (
                <div className="border border-[#FF3B30] bg-[#FF3B30]/5 p-6" data-testid="biggest-dip">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendDown size={20} weight="fill" className="text-[#FF3B30]" />
                    <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">Needs Attention</div>
                  </div>
                  <div className="font-heading font-black text-3xl">{digest.biggest_dip.name}</div>
                  <div className="text-sm text-[#A3A3A3] mt-1">
                    {digest.biggest_dip.missed > 0 && `${digest.biggest_dip.missed} missed workout${digest.biggest_dip.missed > 1 ? "s" : ""}`}
                    {digest.biggest_dip.missed > 0 && digest.biggest_dip.readiness_dip_days > 0 && " · "}
                    {digest.biggest_dip.readiness_dip_days > 0 && `${digest.biggest_dip.readiness_dip_days} readiness dip day${digest.biggest_dip.readiness_dip_days > 1 ? "s" : ""}`}
                  </div>
                </div>
              )}
            </div>

            {/* Player rows */}
            <div className="border border-[#262626] bg-[#121212]">
              <div className="p-4 border-b border-[#262626] text-xs font-heading uppercase tracking-widest text-[#A3A3A3]">Player Breakdown</div>
              <div className="grid grid-cols-12 gap-3 p-3 border-b border-[#262626] text-[10px] font-heading uppercase tracking-widest text-[#A3A3A3]">
                <div className="col-span-3">Player</div>
                <div className="col-span-2 text-center">Sessions</div>
                <div className="col-span-2 text-center">Streak</div>
                <div className="col-span-2 text-center">Readiness</div>
                <div className="col-span-3">Match</div>
              </div>
              {digest.players.map((p) => (
                <div key={p.user_id} className="grid grid-cols-12 gap-3 p-4 border-b border-[#1C1C1C] last:border-0 text-sm items-center" data-testid={`digest-row-${p.user_id}`}>
                  <div className="col-span-3">
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-[#A3A3A3]">{p.position}</div>
                  </div>
                  <div className="col-span-2 text-center">
                    <div className={`font-heading font-black text-2xl ${p.missed > 0 ? "text-[#FF9F0A]" : "text-white"}`}>
                      {p.sessions}<span className="text-sm text-[#A3A3A3]">/{p.planned}</span>
                    </div>
                    {p.missed > 0 && <div className="text-[10px] uppercase tracking-widest text-[#FF9F0A]">{p.missed} missed</div>}
                  </div>
                  <div className="col-span-2 text-center">
                    <div className="inline-flex items-center gap-1">
                      <Flame size={16} weight={p.streak > 0 ? "fill" : "regular"} className={p.streak > 0 ? "text-[#FF3B30]" : "text-[#A3A3A3]"} />
                      <span className="font-heading font-bold">{p.streak}</span>
                    </div>
                  </div>
                  <div className="col-span-2 text-center">
                    {p.readiness_avg !== null ? (
                      <>
                        <div className={`font-heading font-bold text-lg ${
                          p.readiness_avg >= 80 ? "text-[#34C759]" :
                          p.readiness_avg >= 55 ? "text-[#FF9F0A]" : "text-[#FF3B30]"
                        }`}>{p.readiness_avg}</div>
                        {p.readiness_dip_days > 0 && <div className="text-[10px] uppercase tracking-widest text-[#FF3B30]">{p.readiness_dip_days} dip day{p.readiness_dip_days > 1 ? "s" : ""}</div>}
                      </>
                    ) : (
                      <span className="text-xs text-[#A3A3A3]">not logged</span>
                    )}
                  </div>
                  <div className="col-span-3">
                    {p.match ? (
                      <div className="flex items-center gap-2">
                        <span className={`w-7 h-7 flex items-center justify-center font-heading font-black text-xs ${
                          p.match.result === "W" ? "bg-[#34C759] text-white" :
                          p.match.result === "L" ? "bg-[#FF3B30] text-white" : "bg-[#FF9F0A] text-black"
                        }`}>{p.match.result}</span>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold truncate">vs {p.match.opponent}</div>
                          {p.match.self_rating && <div className="text-[10px] text-[#A3A3A3]">{p.match.self_rating}/10</div>}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-[#A3A3A3]">no match</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatBox({ label, value, icon: Icon, color }) {
  return (
    <div className={`border p-4 ${color ? "bg-[#121212]" : "border-[#262626] bg-[#121212]"}`} style={color ? { borderColor: color } : {}}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {Icon && <Icon size={18} weight="duotone" className="mb-2" style={{ color: color || "#FF3B30" }} />}
      <div className="font-heading font-black text-3xl" style={color ? { color } : {}}>{value}</div>
      <div className="text-xs uppercase tracking-widest text-[#A3A3A3] mt-1">{label}</div>
    </div>
  );
}
